import React, { useContext, useEffect, useRef, useState } from "react";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import Pagination from "react-js-pagination";
import { NoteState } from "../../context/notes/NoteState";
import { Modal, Button } from "react-bootstrap";
import moment from "moment";
import jsPDF from "jspdf";
import "jspdf-autotable";

const GOALS = [
  "Fat loss",
  "Muscle gain",
  "Muscle and strength gain",
  "Conditioning",
  "Metabolic issue",
  "Thyroid issues",
  "Post surgery recovery",
  "IBS",
  "Diabetes",
  "Hypertension",
  "PCOs/Hormonal Imbalance",
  "Anemia",
  "H. Pylori",
  "Mineral deficiencies",
  "Food related Allergies",
];

const emptyForm = {
  client_id: "",
  client_name: "",
  diet_plan_issued: false,
  date: moment().format("YYYY-MM-DD"),
  trainer_id: "",
  goal: "",
  remarks: "",
};

const NutritionDietPlans = () => {
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [limit, setLimit] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [trainers, setTrainers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({ ...emptyForm });

  // Client search — same pattern as NutritionAppointments
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientDropdownRef = useRef(null);

  const { state } = useContext(NoteState);
  const { branch_id, UId } = state;

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, limit, searchTerm]);

  useEffect(() => {
    fetchTrainers();
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close client dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // ── Data fetchers ──────────────────────────────────────────────────────────
  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await UserServices.getNutritionDietPlans({
        branch_id,
        nutritionist_id: UId,
        search: searchTerm || undefined,
        limit,
        page: currentPage,
      });
      if (res.status === 200 && res.data.status) {
        const d = res.data;
        setPlans(Array.isArray(d.data) ? d.data : d.data?.data || []);
        setTotalResults(d.pagination?.total_record ?? d.data?.length ?? 0);
      }
    } catch (err) {
      console.log(err);
    }
    setLoading(false);
  };

  const fetchTrainers = async () => {
    try {
      const res = await UserServices.getTrainersForNutrition(branch_id);
      if (res.status === 200 && res.data.status) {
        setTrainers(res.data.data || []);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await UserServices.getClientsName(branch_id);
      if (res.status === 200) {
        const mapped = res.data.data.map((c) => ({
          id: c.id,
          name: `${c.first_name} ${c.last_name}`,
          phone: c.phone,
        }));
        setClients(mapped);
        setFilteredClients(mapped);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleClientSearch = (e) => {
    const v = e.target.value;
    setClientSearchTerm(v);
    setShowClientDropdown(true);
    setFilteredClients(
      v.trim() === ""
        ? clients
        : clients.filter(
            (c) =>
              c.name.toLowerCase().includes(v.toLowerCase()) ||
              (c.phone && c.phone.includes(v))
          )
    );
  };

  const handleClientSelect = (client) => {
    setFormData((prev) => ({ ...prev, client_id: client.id, client_name: client.name }));
    setClientSearchTerm(client.name);
    setShowClientDropdown(false);
  };

  // ── Modal helpers ──────────────────────────────────────────────────────────
  const handleOpenAdd = () => {
    setFormData({ ...emptyForm });
    setClientSearchTerm("");
    setFilteredClients(clients);
    setEditMode(false);
    setSelectedId(null);
    setShowModal(true);
  };

  const handleOpenEdit = (plan) => {
    const clientName = plan.client
      ? `${plan.client.first_name} ${plan.client.last_name}`
      : "";
    setFormData({
      client_id:         plan.client_id || "",
      client_name:       clientName,
      diet_plan_issued:  Boolean(plan.diet_plan_issued),
      date:              plan.date ? moment(plan.date).format("YYYY-MM-DD") : "",
      trainer_id:        plan.trainer_id || "",
      goal:              plan.goal || "",
      remarks:           plan.remarks || "",
    });
    setClientSearchTerm(clientName);
    setFilteredClients(clients);
    setEditMode(true);
    setSelectedId(plan.id);
    setShowModal(true);
  };

  const handleClose = () => setShowModal(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_id) { alert("Please select a client."); return; }

    const payload = {
      branch_id,
      nutritionist_id: UId,
      client_id:        formData.client_id,
      diet_plan_issued: formData.diet_plan_issued,
      date:             formData.date,
      trainer_id:       formData.trainer_id || null,
      goal:             formData.goal || null,
      remarks:          formData.remarks || null,
    };

    try {
      if (editMode) {
        await UserServices.updateNutritionDietPlan(selectedId, payload);
      } else {
        await UserServices.createNutritionDietPlan(payload);
      }
      handleClose();
      fetchPlans();
    } catch (err) {
      console.log(err);
      alert(err.response?.data?.message || "Save failed.");
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await UserServices.deleteNutritionDietPlan(id);
      fetchPlans();
    } catch (err) {
      console.log(err);
    }
  };

  // ── Export ─────────────────────────────────────────────────────────────────
  const [exporting, setExporting] = useState(false);

  const handleExcelExport = async () => {
    setExporting(true);
    try {
      const response = await UserServices.exportNutritionDietPlans({
        branch_id,
        nutritionist_id: UId,
        search: searchTerm || undefined,
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Diet_Plans_Issued_${moment().format("YYYY-MM-DD")}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.log(err);
      alert("Error exporting diet plans.");
    } finally {
      setExporting(false);
    }
  };

  const [pdfLoading, setPdfLoading] = useState(false);

  const handlePdfExport = async () => {
    setPdfLoading(true);
    try {
      const res = await UserServices.getNutritionDietPlans({
        branch_id,
        nutritionist_id: UId,
        search: searchTerm || undefined,
      });
      const allPlans = Array.isArray(res.data.data) ? res.data.data : [];
      const doc = new jsPDF("l", "mm", "a4");
      doc.setFontSize(16);
      doc.text("Diet Plans Issued Report", 14, 18);
      doc.setFontSize(10);
      doc.text(`Generated: ${moment().format("DD MMM YYYY")}`, 14, 26);
      doc.autoTable({
        startY: 32,
        head: [["S#", "Date", "Client Name", "Diet Plan", "Goal", "Trainer", "Remarks"]],
        body: allPlans.map((p, i) => [
          i + 1,
          p.date ? moment(p.date).format("DD/MM/YYYY") : "",
          p.client ? `${p.client.first_name} ${p.client.last_name}` : "N/A",
          p.diet_plan_issued ? "Issued" : "Pending",
          p.goal || "N/A",
          p.trainer ? `${p.trainer.first_name} ${p.trainer.last_name}` : "N/A",
          p.remarks || "",
        ]),
        headStyles: { fillColor: [26, 92, 69], textColor: 255 },
        alternateRowStyles: { fillColor: [240, 248, 244] },
        styles: { fontSize: 8 },
      });
      doc.save(`Diet_Plans_Issued_${moment().format("YYYY-MM-DD")}.pdf`);
    } catch (err) {
      console.log(err);
      alert("Error generating PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="site-main-design">
      <MenuButton />
      <div className="container-fluid">
        <div className="row mb-3">
          <div className="col-12">
            <div className="page-heading">
              <h3>
                <i className="bi bi-journal-medical me-2"></i>Diet Plan Issued
              </h3>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body py-2">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md-5">
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-white">
                    <i className="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search client..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  />
                  {searchTerm && (
                    <button className="btn btn-outline-secondary" onClick={() => setSearchTerm("")}>
                      <i className="bi bi-x"></i>
                    </button>
                  )}
                </div>
              </div>
              <div className="col-auto ms-auto d-flex gap-2">
                <button
                  className="btn btn-success btn-sm"
                  onClick={handleExcelExport}
                  disabled={exporting || pdfLoading}
                  title="Download Excel"
                >
                  <i className="bi bi-file-earmark-excel me-1"></i>
                  {exporting ? "Exporting..." : "Excel"}
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={handlePdfExport}
                  disabled={exporting || pdfLoading}
                  title="Download PDF"
                >
                  <i className="bi bi-file-earmark-pdf me-1"></i>
                  {pdfLoading ? "Generating..." : "PDF"}
                </button>
                <button className="btn btn-primary btn-sm" onClick={handleOpenAdd}>
                  <i className="bi bi-plus-circle me-1"></i>Add Record
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card border-0 shadow-sm">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-dark">
                  <tr>
                    <th className="text-center" style={{ width: 60 }}>S#</th>
                    <th>Client Name</th>
                    <th className="text-center">Diet Plan</th>
                    <th>Date</th>
                    <th>Trainer</th>
                    <th>Goal</th>
                    <th className="text-center" style={{ width: 100 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="text-center py-5">
                        <div className="spinner-border text-primary" role="status" />
                      </td>
                    </tr>
                  ) : plans.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-5 text-muted">
                        <i className="bi bi-inbox fs-2 d-block mb-2"></i>
                        No records found
                      </td>
                    </tr>
                  ) : (
                    plans.map((plan, idx) => {
                      const clientName = plan.client
                        ? `${plan.client.first_name} ${plan.client.last_name}`
                        : "—";
                      const trainerName = plan.trainer
                        ? `${plan.trainer.first_name} ${plan.trainer.last_name}`
                        : "—";
                      return (
                        <tr key={plan.id}>
                          <td className="text-center text-muted small">
                            {(currentPage - 1) * limit + idx + 1}
                          </td>
                          <td className="fw-semibold">{clientName}</td>
                          <td className="text-center">
                            {plan.diet_plan_issued ? (
                              <span className="badge bg-success">
                                <i className="bi bi-check-lg me-1"></i>Issued
                              </span>
                            ) : (
                              <span className="badge bg-secondary">Pending</span>
                            )}
                          </td>
                          <td className="text-nowrap">
                            {plan.date ? moment(plan.date).format("DD/MM/YYYY") : "—"}
                          </td>
                          <td>{trainerName}</td>
                          <td>
                            {plan.goal ? (
                              <span
                                className="badge rounded-pill"
                                style={{ background: "#0d6efd22", color: "#0d6efd", fontWeight: 500 }}
                              >
                                {plan.goal}
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-outline-primary me-1"
                              title="Edit"
                              onClick={() => handleOpenEdit(plan)}
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              title="Delete"
                              onClick={() => handleDelete(plan.id)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {totalResults > limit && (
            <div className="card-footer bg-transparent d-flex justify-content-between align-items-center flex-wrap gap-2">
              <small className="text-muted">
                Showing {(currentPage - 1) * limit + 1}–{Math.min(currentPage * limit, totalResults)} of{" "}
                {totalResults}
              </small>
              <Pagination
                activePage={currentPage}
                itemsCountPerPage={limit}
                totalItemsCount={totalResults}
                pageRangeDisplayed={5}
                onChange={(page) => setCurrentPage(page)}
                itemClass="page-item"
                linkClass="page-link"
                innerClass="pagination pagination-sm mb-0"
                firstPageText="«"
                lastPageText="»"
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      <Modal show={showModal} onHide={handleClose} centered size="lg">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>
            <i className={`bi ${editMode ? "bi-pencil-square" : "bi-plus-circle"} me-2`}></i>
            {editMode ? "Edit Diet Plan Record" : "Add Diet Plan Record"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSubmit} id="diet-plan-form">
            <div className="row g-3">

              {/* Client search */}
              <div className="col-12 col-md-6">
                <label className="form-label fw-semibold">
                  Client <span className="text-danger">*</span>
                </label>
                <div className="position-relative" ref={clientDropdownRef}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Type to search client by name or phone..."
                    value={clientSearchTerm}
                    onChange={handleClientSearch}
                    onFocus={() => { setShowClientDropdown(true); setFilteredClients(clients); }}
                    autoComplete="off"
                  />
                  {showClientDropdown && filteredClients.length > 0 && (
                    <div
                      className="position-absolute w-100 bg-white border rounded shadow-sm"
                      style={{ zIndex: 1050, maxHeight: 250, overflowY: "auto", top: "100%", marginTop: 2 }}
                    >
                      {filteredClients.map((c) => (
                        <div
                          key={c.id}
                          className="p-2 border-bottom"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleClientSelect(c)}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8f9fa")}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}
                        >
                          <div className="fw-semibold">{c.name}</div>
                          {c.phone && <small className="text-muted">{c.phone}</small>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {formData.client_id && (
                  <small className="text-success">
                    <i className="bi bi-check-circle me-1"></i>Client selected
                  </small>
                )}
              </div>

              {/* Date */}
              <div className="col-12 col-md-6">
                <label className="form-label fw-semibold">
                  Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {/* Trainer */}
              <div className="col-12 col-md-6">
                <label className="form-label fw-semibold">Trainer</label>
                <select
                  className="form-select"
                  name="trainer_id"
                  value={formData.trainer_id}
                  onChange={handleInputChange}
                >
                  <option value="">— Select Trainer —</option>
                  {trainers.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.first_name} {t.last_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Goal */}
              <div className="col-12 col-md-6">
                <label className="form-label fw-semibold">Goal</label>
                <select
                  className="form-select"
                  name="goal"
                  value={formData.goal}
                  onChange={handleInputChange}
                >
                  <option value="">— Select Goal —</option>
                  {GOALS.map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>

              {/* Diet Plan Issued checkbox */}
              <div className="col-12 col-md-6 d-flex align-items-center">
                <div className="form-check form-switch ms-1">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="diet_plan_issued"
                    name="diet_plan_issued"
                    checked={formData.diet_plan_issued}
                    onChange={handleInputChange}
                    style={{ width: "3rem", height: "1.5rem" }}
                  />
                  <label className="form-check-label fw-semibold ms-2" htmlFor="diet_plan_issued">
                    Diet Plan Issued
                  </label>
                </div>
              </div>

              {/* Remarks */}
              <div className="col-12">
                <label className="form-label fw-semibold">Remarks</label>
                <textarea
                  className="form-control"
                  name="remarks"
                  rows={2}
                  placeholder="Optional notes..."
                  value={formData.remarks}
                  onChange={handleInputChange}
                />
              </div>

            </div>
          </form>
        </Modal.Body>
        <Modal.Footer className="border-top">
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="diet-plan-form">
            <i className="bi bi-save me-1"></i>
            {editMode ? "Update" : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default NutritionDietPlans;
